import React, { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { trpcClient } from "@/lib/trpc";
import PurchasingForm, { type PurchasingFormData } from "./PurchasingForm";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";

export default function PurchasingCreate() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as any;

  const prIds = searchParams?.prIds ? (Array.isArray(searchParams.prIds) ? searchParams.prIds : [searchParams.prIds]) : [];
  
  // Fetch PRs if provided
  const { data: prsData } = useQuery({
    queryKey: ["purchase-requests-prefill", prIds],
    queryFn: async () => {
      if (!prIds || prIds.length === 0) return [];
      return await trpcClient.mrp.getPurchaseRequestsByIds.query({ ids: prIds });
    },
    enabled: prIds.length > 0,
  });

  // Từ hàng đợi thiếu hụt / kế hoạch vật tư: mỗi dòng kế hoạch thành 1 dòng đơn mua gắn đúng dòng kế hoạch
  // (giữ chỗ số cần mua, không gộp các vật tư khác nhau vào một dòng — rà soát 31/10/2026)
  const planIds: string[] = String(searchParams?.materialPlanIds || searchParams?.materialPlanId || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  const { data: plansData } = useQuery({
    queryKey: ["material-plans-prefill", planIds],
    queryFn: async () => Promise.all(planIds.map((id) => trpcClient.materialPlanning.getById.query({ id }))),
    enabled: planIds.length > 0 && prIds.length === 0,
  });

  const defaultValues = useMemo<Partial<PurchasingFormData>>(() => {
    if (plansData && plansData.length > 0) {
      return {
        vendorId: searchParams?.vendorId || "",
        notes: `Đơn mua theo ${plansData.length} dòng kế hoạch vật tư`,
        items: plansData.map((plan: any) => {
          const material = plan.plannedMaterial || plan.originalMaterial;
          const quantity = Math.max(1, Math.ceil(Number(plan.qtyToOrder) || 0));
          const unitPrice = Number(material?.unitPrice || 0);
          return {
            materialId: plan.plannedMaterialId || plan.originalMaterialId,
            materialName: material?.name || "",
            quantity,
            unitPrice,
            totalAmount: quantity * unitPrice,
            productionOrderId: plan.productionOrderId || undefined,
            lsxId: plan.productionOrder?.orderId || undefined,
            materialPlanId: plan.id,
          };
        }),
      };
    }
    if (prsData && prsData.length > 0) {
      // Group by Vendor? We assume they pick PRs for a single vendor, or we just leave vendorId empty for the user to select
      return {
        vendorId: prsData[0].material?.vendorId || "",
        notes: `Nạp tự động từ ${prsData.length} Yêu cầu mua hàng (PR)`,
        items: prsData.map((pr: any) => ({
          materialId: pr.materialId,
          materialName: pr.material?.name || "",
          quantity: Number(pr.quantity),
          unitPrice: Number(pr.material?.unitPrice || 0),
          totalAmount: Number(pr.quantity) * Number(pr.material?.unitPrice || 0),
          productionOrderId: pr.productionOrderId || undefined,
          lsxId: pr.productionOrder?.orderId || undefined,
          materialPlanId: pr.materialPlanId || undefined,
        })),
      };
    }
    
    // Fallback to legacy single search param
    const materialId = searchParams?.materialId || "";
    const quantity = Number(searchParams?.quantity) || 0;
    
    return {
      vendorId: searchParams?.vendorId || "",
      items: [
        {
          materialId,
          quantity: quantity > 0 ? quantity : 1,
          unitPrice: 0,
          totalAmount: 0,
        }
      ]
    };
  }, [plansData, prsData, searchParams]);

  const handleSubmit = async (data: any) => {
    try {
      await trpcClient.purchaseOrders.create.mutate(data);
      toast.success("Tạo đơn mua hàng thành công!");
      navigate({ to: "/purchasing" });
    } catch (e: any) {
      toast.error(e.message || "Tạo đơn mua vật tư thất bại");
    }
  };

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Đơn Mua Hàng", to: "/purchasing" },
        { label: "Tạo mới" },
      ]}
      title="Tạo Đơn Mua Hàng"
      onBack={() => navigate({ to: "/purchasing" })}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 p-4 animate-in fade-in duration-300">
        {prIds.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900">
            <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              <strong>Đã nạp tự động từ {prIds.length} Yêu cầu mua hàng:</strong> Điền sẵn thông tin vật tư, số lượng thiếu hụt.
            </span>
          </div>
        )}

        {/* Re-mount form when defaultValues changes (e.g. data loaded) */}
        {(!prIds.length || prsData) && (!planIds.length || plansData) && (
          <PurchasingForm
            key={prsData || plansData ? 'loaded' : 'empty'}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onCancel={() => navigate({ to: "/purchasing" })}
            submitLabel="Lưu"
          />
        )}
      </div>
    </StandardDetailLayout>
  );
}
